import { GQL } from '@aline/graphql';
import { z } from 'zod';

import { ReportGridFilterModelSchema } from './report-filter';
import { reportPreviewColumnsSchema, reportPreviewFilterSchema } from './report-preview';

const ReportPlanBaseSchema = z.object({
  columns: reportPreviewColumnsSchema,
  fileFields: z.array(z.nativeEnum(GQL.ReportColumnFileField)).optional().default([]),
  goal: z.string(),
  /**
   * Optional user-facing instructions for filtering inside the report once
   * extraction has run — for asks the plan's document filters cannot express
   * (e.g. "filter Expiration Date on or before today" for expired contracts).
   * Emitted only when the request needs such a post-creation step.
   */
  howToUse: z.string().optional(),
  name: z.string(),
});

/** Human-readable proposal emitted by the report agent. */
export const ReportPlanProposalSchema = ReportPlanBaseSchema.extend({
  filters: z.array(reportPreviewFilterSchema).optional().default([]),
  logicOperator: z.enum(['and', 'or']).optional().default('and'),
});
export type ReportPlanProposal = z.infer<typeof ReportPlanProposalSchema>;
export type ReportPlanProposalInput = z.input<typeof ReportPlanProposalSchema>;

/**
 * Approval-card plan after the server has resolved entity names and rewritten
 * semantic operators into the canonical Report grid filter model.
 */
export const ResolvedReportPlanSchema = ReportPlanBaseSchema.extend({
  filterModel: ReportGridFilterModelSchema,
});
export type ResolvedReportPlan = z.infer<typeof ResolvedReportPlanSchema>;
export type ResolvedReportPlanInput = z.input<typeof ResolvedReportPlanSchema>;

/** Backwards-compatible aliases for the AI proposal contract. */
export const ReportPlanSchema = ReportPlanProposalSchema;
export type ReportPlan = ReportPlanProposal;
export type ReportPlanInput = ReportPlanProposalInput;

/**
 * The chat card for a report the AI created from an approved plan. The report
 * exists when the card is emitted; the card is a link to that report.
 */
export const ReportAiGenerationResultSchema = z.object({
  columnCount: z.number().int().nonnegative(),
  name: z.string(),
  reportId: z.uuid(),
  type: z.literal('report-generation'),
});
export type ReportAiGenerationResult = z.infer<typeof ReportAiGenerationResultSchema>;
